import { useTranslation } from "react-i18next";
import StatusBadge from "../../shared/Ui/StatusBadge/StatusBadge";
import { useEffect, useState, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import * as XLSX from 'xlsx';
import { SkeletonTheme } from "react-loading-skeleton";
import { EmptyListPlaceholderContent } from "../../shared/Ui/EmptyPageBlock";
import MockSkeleton from "../RegisrationUsers/utils/MockSkeleton";
import { Field, Form, Formik } from "formik";
import { PaginationState } from "@tanstack/react-table";
import CalendarSelectFormik from "../../shared/Ui/Calendar/ CalendarSelectFormik";
import SelectCustomFormik from "../../shared/Ui/SelectCustom/SelectCustomFormik";
import style from "./index.module.css";
import exportToExcel from "./utils/exportExcel";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import Cookies from "js-cookie";
import { getConsumerList, getOfdList } from "../../common/api/checkApi";
import TableLoader from "../../shared/Ui/Loader/TableLoader";
import AlertBox from "../../shared/Ui/AlertBox/AlertBox";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const MAX_RANGE_MONTHS = 6;

const getWeekFilter = () => {
  const now = dayjs().tz("Asia/Oral");
  return {
    startDate: now.subtract(7, "day").startOf("day").toISOString(),
    endDate: now.toISOString(),
    startTime: "00:00:00",
    endTime: now.format("HH:mm:ss"),
  };
};

const getHalfYearFilter = () => {
  const now = dayjs().tz("Asia/Oral");
  // Чуть меньше 6 месяцев — бэкенд отклоняет ровно 6 месяцев (DATE_RANGE_TOO_LARGE)
  return {
    startDate: now.subtract(MAX_RANGE_MONTHS, "month").add(1, "day").startOf("day").toISOString(),
    endDate: now.toISOString(),
    startTime: "00:00:00",
    endTime: now.format("HH:mm:ss"),
  };
};

const isDateRangeTooLarge = (start: any, end: any) => {
  if (!start || !end) return false;
  const startDay = dayjs(start);
  const endDay = dayjs(end);
  if (!startDay.isValid() || !endDay.isValid()) return false;
  // Диапазон не должен достигать/превышать 6 месяцев
  return !endDay.isBefore(startDay.add(MAX_RANGE_MONTHS, "month"));
};

function MainScreen() {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [ofdList, setOfdList] = useState<any>(null);
  const [alertOpen, setAlertOpen] = useState<any>(null);
  const [dataObject, setDataObject] = useState<any>({
    startDate: undefined,
    startTime: "",
    endDate: undefined,
    endTime: "",
  });
  const [filterData, setFilterData] = useState<any>(() => getWeekFilter());
  const [initialValues, setInitialValues] = useState({
    selectorData: "week" as string | null,
    pickerData: null,
  });
  const [data, setData] = useState<any>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const socketCountRef = useRef<WebSocket | null>(null);

  // Логирование изменений состояний
  useEffect(() => {
    console.log("🟡 Состояние изменилось: isLoading=", isLoading, "isDataLoaded=", isDataLoaded, "data=", !!data, "data?.binOfd=", data?.binOfd);
  }, [isLoading, isDataLoaded, data]);

  const transformData = (backendData: any): {
    seriesOrb: any[][],
    labelsOrb: any[][],
    seriesBar: { name: string; data: number[] }[][],
    binOfd: string[]
  } => {
    const seriesOrb: any[][] = [];
    const labelsOrb: any[][] = [];
    const seriesBar: { name: string; data: number[] }[][] = [];
    const binOfd: string[] = [];

    Object.entries(backendData).forEach(([bin, companyData]: [string, any]) => {
      const series: number[] = [];
      const label: string[] = [];
      const sentSeries: number[] = [];
      const errorSeries: number[] = [];

      Object.entries(companyData).forEach(([key, value]: [string, any]) => {
        const errorCount: number = value.ERROR || 0;
        const sentCount: number = value.SENT || 0;
        const totalSent: number = sentCount + errorCount;

        series.push(sentCount);
        label.push(key);
        sentSeries.push(totalSent);
        errorSeries.push(errorCount);
      });

      seriesOrb.push(series);
      labelsOrb.push(label);
      seriesBar.push([
        { name: "Отправленные", data: sentSeries },
        { name: "Ошибочные", data: errorSeries }
      ]);
      binOfd.push(bin); // Добавляем BIN в массив
    });

    return { seriesOrb, labelsOrb, seriesBar, binOfd };
  };

  const handleSubmit = (values: any) => {
    console.log("🔵 handleSubmit вызван", values);
    const now = dayjs().tz("Asia/Oral"); // Западный Казахстан (Уральск)

    let startDate, endDate, startTime, endTime;

    switch (values.selectorData) {
      case "today":
        startDate = now.startOf("day").toISOString();
        endDate = now.toISOString();
        startTime = "00:00:00";
        endTime = now.format("HH:mm:ss");
        break;

      case "week":
        startDate = now.subtract(7, "day").startOf("day").toISOString();
        endDate = now.toISOString();
        startTime = "00:00:00";
        endTime = now.format("HH:mm:ss");
        break;

      case "month":
        startDate = now.subtract(1, "month").startOf("day").toISOString();
        endDate = now.toISOString();
        startTime = "00:00:00";
        endTime = now.format("HH:mm:ss");
        break;

      case "halfYear": {
        const halfYear = getHalfYearFilter();
        startDate = halfYear.startDate;
        endDate = halfYear.endDate;
        startTime = halfYear.startTime;
        endTime = halfYear.endTime;
        break;
      }

      default:
        // Если выбрана "Своя дата" - используем значения из dataObject
        startDate = dataObject.startDate;
        endDate = dataObject.endDate;
        startTime = dataObject.startTime;
        endTime = dataObject.endTime;

        if (isDateRangeTooLarge(startDate, endDate)) {
          setAlertOpen({
            status: "error",
            message: "Диапазон дат не должен превышать 6 месяцев",
          });
          return;
        }
        break;
    }

    console.log("🔵 handleSubmit: устанавливаем состояние загрузки");
    setIsLoading(true);
    setIsDataLoaded(false);
    setData(null); // Очищаем текущие данные графиков
    console.log("🔵 handleSubmit: устанавливаем filterData", { startDate, startTime, endDate, endTime });
    setFilterData({
      startDate,
      startTime,
      endDate,
      endTime,
    });
    console.log("🔵 handleSubmit: состояние установлено, isLoading должен быть true");
  };

  useEffect(() => {
    getOfdList().then((response: any) => {
      const ofdDictionary: Record<string, string> = {};

      response?.ofds?.forEach((item: any) => {
        ofdDictionary[item.bin] = item.name;
      });

      setOfdList(ofdDictionary);

    });
  }, []);

  console.log(5555, data);
  

  useEffect(() => {
    console.log("🟢 useEffect dashboard вызван, ofdList:", !!ofdList, "filterData:", filterData);
    if (!ofdList) {
      console.log("🟢 useEffect: ofdList еще не загружен, выходим");
      return; // Ждём, пока загрузится список ОФД
    }

    const token = Cookies.get("access_token");
    if (!token) {
      console.error("Токен не найден в cookies");
      setIsLoading(false);
      setIsDataLoaded(true);
      return;
    }

    // Закрываем предыдущее соединение если оно есть
    if (socketRef.current) {
      console.log("🟢 useEffect: закрываем предыдущее WebSocket соединение");
      socketRef.current.close();
      socketRef.current = null;
    }

    console.log("🟢 useEffect: устанавливаем состояние загрузки - isLoading=true, isDataLoaded=false");
    setIsLoading(true);
    setIsDataLoaded(false);
    setData(null); // Очищаем данные перед новым запросом
    console.log("🟢 useEffect: данные очищены, создаем новое WebSocket соединение");

    // Формируем параметры времени
    const params = [];

    if (filterData?.startDate && filterData?.startTime) {
      const startDateTime = new Date(filterData.startDate);
      const [hours, minutes, seconds] = filterData.startTime.split(":");
      startDateTime.setHours(+hours, +minutes, +seconds);

      // Добавляем 5 часов
      startDateTime.setHours(startDateTime.getHours() + 5);

      params.push(`startDate=${encodeURIComponent(startDateTime.toISOString().slice(0, -5))}`);
    }

    if (filterData?.endDate && filterData?.endTime) {
      const endDateTime = new Date(filterData.endDate);
      const [hours, minutes, seconds] = filterData.endTime.split(":");
      endDateTime.setHours(+hours, +minutes, +seconds);

      // Добавляем 5 часов
      endDateTime.setHours(endDateTime.getHours() + 5);

      params.push(`endDate=${encodeURIComponent(endDateTime.toISOString().slice(0, -5))}`);
    }

    // Добавляем параметры к URL
    const queryString = params.length ? `&${params.join("&")}` : "";
    const socketUrl = `${process.env.REACT_APP_URL_MAIN_WWS}/operator/ws/dashboard?token=${token}${queryString}`;

    const socket: WebSocket = new WebSocket(socketUrl);
    socketRef.current = socket;

    socket.onopen = () => {
      console.log("🟢 WebSocket открыт:", socketUrl);
    };

    socket.onmessage = (event: MessageEvent) => {
      // Проверяем, что это сообщение от текущего активного соединения
      if (socketRef.current !== socket) {
        console.log("🟢 WebSocket: игнорируем сообщение от старого соединения");
        return;
      }
      try {
        console.log("🟢 WebSocket получил сообщение");
        const rawData: any = JSON.parse(event.data);
        console.log("🟢 WebSocket данные:", rawData);

        if (rawData?.error) {
          setAlertOpen({
            status: "error",
            message:
              rawData?.message ||
              (rawData?.error === "DATE_RANGE_TOO_LARGE"
                ? "Диапазон дат не должен превышать 6 месяцев"
                : "Ошибка загрузки данных дашборда"),
          });
          setData(null);
          setIsLoading(false);
          setIsDataLoaded(true);
          return;
        }

        // Проверяем, пуст ли объект (Object.keys().length === 0)
        if (Object.keys(rawData).length === 0) {
          console.log("🟢 WebSocket: данные пустые, устанавливаем binOfd: []");
          setData((prevData: any) => ({ 
            ...prevData, 
            binOfd: [] 
          })); // Сохраняем предыдущие данные (countSuccess, countError, countGroup)
        } else {
          console.log("🟢 WebSocket: данные получены, преобразуем и устанавливаем");
          const transformedData = transformData(rawData);
          setData((prevData: any) => ({ 
            ...transformedData,
            // Сохраняем countSuccess, countError, countGroup из предыдущих данных (они приходят из второго WebSocket)
            countSuccess: prevData?.countSuccess || {},
            countError: prevData?.countError || {},
            countGroup: prevData?.countGroup || {},
          })); 
        }
        console.log("🟢 WebSocket: устанавливаем isLoading=false, isDataLoaded=true");
        setIsLoading(false);
        setIsDataLoaded(true);
      } catch (error) {
        console.error("🟢 Ошибка обработки сообщения:", error);
        if (socketRef.current === socket) {
          setIsLoading(false);
          setIsDataLoaded(true);
        }
      }
    };

    socket.onclose = (event: CloseEvent) => {
      console.log("🟢 WebSocket соединение закрыто", event, "socketRef.current === socket:", socketRef.current === socket);
      // Обновляем состояние только если это текущее активное соединение
      if (socketRef.current === socket) {
        socketRef.current = null;
        // Не устанавливаем isLoading=false здесь, так как может быть новое соединение уже создано
        // Состояние будет обновлено в onmessage нового соединения
      }
    };

    socket.onerror = (error) => {
      console.error("🟢 WebSocket ошибка:", error, "socketRef.current === socket:", socketRef.current === socket);
      // Обновляем состояние только если это текущее активное соединение
      if (socketRef.current === socket) {
        setIsLoading(false);
        setIsDataLoaded(true);
      }
    };

    return () => {
      if (socketRef.current === socket) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [ofdList, filterData]);



  useEffect(() => {
    if (!ofdList) return; // Ждём, пока загрузится список ОФД

    const token = Cookies.get("access_token");
    if (!token) {
      return;
    }

    // Закрываем предыдущее соединение если оно есть
    if (socketCountRef.current) {
      socketCountRef.current.close();
      socketCountRef.current = null;
    }

    // Формируем параметры времени
    const params = [];

    
if (filterData?.startDate && filterData?.startTime) {
  const startDateTime = new Date(filterData.startDate);
  const [hours, minutes, seconds] = filterData.startTime.split(":");
  startDateTime.setHours(+hours, +minutes, +seconds);

  // Добавляем 5 часов
  startDateTime.setHours(startDateTime.getHours() + 5);

  params.push(`startDate=${encodeURIComponent(startDateTime.toISOString().slice(0, -5))}`);
}

if (filterData?.endDate && filterData?.endTime) {
  const endDateTime = new Date(filterData.endDate);
  const [hours, minutes, seconds] = filterData.endTime.split(":");
  endDateTime.setHours(+hours, +minutes, +seconds);

  // Добавляем 5 часов
  endDateTime.setHours(endDateTime.getHours() + 5);

  params.push(`endDate=${encodeURIComponent(endDateTime.toISOString().slice(0, -5))}`);
}


    // Добавляем параметры к URL
    const queryString = params.length ? `&${params.join("&")}` : "";
    const socketUrl = `${process.env.REACT_APP_URL_MAIN_WWS}/operator/ws/dashboard-count?token=${token}${queryString}`;



    const socket: WebSocket = new WebSocket(socketUrl);
    socketCountRef.current = socket;

    socket.onmessage = (event: MessageEvent) => {
      try {
        console.log("Второй WebSocket:", event.data, queryString || '', socketUrl);

        const rawData: any = JSON.parse(event.data);

        if (rawData?.error) {
          setAlertOpen({
            status: "error",
            message: rawData?.message || "Ошибка загрузки данных дашборда",
          });
          return;
        }

        setData((prevData: any) => ({
          ...prevData,
          countGroup: rawData.countGroup || {},
          countSuccess: rawData.countSuccess || {},
          countError: rawData.countError || {},
        }));
      } catch (error) {
        console.error("Ошибка обработки сообщения второго WebSocket:", error);
      }
    };

    socket.onclose = (event: CloseEvent) => {
      console.log("WebSocket соединение закрыто", event);
      if (socketCountRef.current === socket) {
        socketCountRef.current = null;
      }
    };

    socket.onerror = (error) => {
      console.error("WebSocket ошибка:", error);
    };

    return () => {
      if (socketCountRef.current === socket) {
        socketCountRef.current.close();
        socketCountRef.current = null;
      }
    };
  }, [ofdList, filterData]);


  const chartOptions: { series: number[]; options: ApexOptions } = {
    series: [60000, 80000, 100000], // Значения для каждого сегмента
    options: {
      chart: {
        type: 'polarArea',
        height: 700,
        foreColor: '#000',
        animations: {
          enabled: false, // Выключаем анимацию
        },
      },
      stroke: {
        colors: ['#fff'],
      },
      fill: {
        opacity: 0.8,
      },
      colors: [
        '#00b074', // Зеленый  
        '#125dae', // Синий  
        '#ffcd30', // Желтый  
        '#ff5733', // Оранжево-красный  
        '#9b59b6', // Фиолетовый  
        '#2ecc71', // Светло-зеленый  
        '#3498db', // Голубой  
        '#e74c3c', // Красный  
        '#f39c12', // Темно-желтый  
        '#1abc9c'  // Бирюзовый  
      ],
      labels: ['ИС МПТ', 'ЮИ', 'ЦЭДМ'], // Названия серий
      legend: {
        position: 'bottom',
        show: true, // Включаем легенду
        formatter: function (seriesName, opts) {
          // Форматируем отображение легенды
          const value = opts.w.config.series[opts.seriesIndex];
          return `${seriesName}: ${value}`;
        },
      },
      dataLabels: {
        enabled: false,
        formatter: (val, opts) => {
          return `${val}`;
        },
      },
      xaxis: {
        labels: {
          style: {
            fontSize: '14px',  // Увеличиваем размер шрифта для меток на оси X
            fontWeight: 'bold', // Жирный шрифт для оси X
          }
        }
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '14px',  // Увеличиваем размер шрифта для меток на оси Y
            fontWeight: 'bold', // Жирный шрифт для оси Y
          }
        }
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 200,
            },
          },
        },
      ],
    },
  };


  const barOptions: { series: { data: number[] }[]; options: ApexOptions } = {
    series: [
      {
        data: [125000, 55000], // Значения для "Успешные" и "Ошибочные"
      },
    ],
    options: {
      chart: {
        height: 350,
        type: 'bar',
        toolbar: {
          show: false, // Отключает панель инструментов, включая бургер-меню
        },
      },
      plotOptions: {
        bar: {
          columnWidth: '50%', // Ширина колонок
          barHeight: '100%',
          distributed: true,
        },
      },
      dataLabels: {
        enabled: true, // Включить отображение значений
        formatter: (val: number) => `${val}`, // Формат значения
      },
      legend: {
        position: 'bottom', // Легенда внизу
        formatter: function(seriesName: string, opts: any) {
          return `${seriesName}: ${opts.w.config.series[0].data[opts.seriesIndex]}`;
        }
      },
      xaxis: {
        categories: ['Успешные', 'Ошибочные'], // Названия колонок
        labels: {
          show: false, // Включить метки на оси X (названия категорий)
        },
      },
      yaxis: {
        show: false, // Убрать ось Y
      },
      grid: {
        show: false, // Убрать сетку
      },
      colors: ['#00b074', '#FF4560'], // Цвета колонок: зеленый и красный
      tooltip: {
        enabled: false, // Включить тултип
        y: {
          formatter: (val: number) => `${val}`, // Показать только значение
        },
        x: {
          formatter: (val: number) => `${val}`, // Показать название категории
        },
      },
    },
  };

  const barDifferenceConfig: { series: any; options: ApexOptions } = {

    series: [{
      name: 'Отправленные',
      data: [130000, 85000, 101000]
    }, {
      name: 'Ошибочные',
      data: [76000, 45000, 51000]
    }],
    options: {
      chart: {
        type: 'bar',
        height: 350,
        toolbar: {
          show: false, // Отключаем тулбар
        },
      },
      plotOptions: {
        bar: {
          horizontal: true,

          barHeight: '20px',
          borderRadius: 0,
          borderRadiusApplication: 'end'
        },
      },
      colors: ['#00b074', '#FF4560'],
      dataLabels: {
        enabled: true, // Включаем отображение значений
        style: {
          colors: ['#FFFFFF'], // Указываем белый цвет текста
          fontSize: '12px', // Размер шрифта для лучшей читаемости
          fontWeight: 'bold',
        },
        formatter: function (val) {
          return `${val}`; // Форматирование значения
        },
        offsetX: -10000,
        textAnchor: 'start',
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent']
      },
      xaxis: {
        categories: ['ИС МПТ', 'ЦЭДМ', 'ЮИ'],
        tickAmount: 7,
      },
      grid: {
        show: true, // Включаем сетку
        xaxis: {
          lines: {
            show: true, // Показываем вертикальные линии
          },
        },
        yaxis: {
          lines: {
            show: false, // Отключаем горизонтальные линии
          },
        },
      },
      yaxis: {
        show: true,
        labels: {
          offsetX: -10,
          align: 'left',
          maxWidth: 70,
          minWidth: 70
        }
      },
      fill: {
        opacity: 1
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return `${val}`
          }
        }
      }
    }
  }



  const getReportTitle = () => {
    if (!dataObject.endDate) {
      return `Отчет за ${dayjs().format("DD.MM.YYYY")}`;
    }

    const startDate = dayjs(dataObject.startDate).format("DD.MM.YYYY");
    const endDate = dayjs(dataObject.endDate).format("DD.MM.YYYY");

    return `Количественный отчет по чекам маркированных товаров с ${startDate} по ${endDate}`;
  };


  return (
    <div className="w-full h-full">
      <div className={`rounded-lg  bg-white`}>
        <div className="bg-white w-full  my-5 p-5  pt-8 relative rounded-xl shadow-md flex flex-wrap flex-row align-center gap-[20px]">
          <Formik
            initialValues={initialValues}
            onSubmit={(formik: any) => {
              handleSubmit(formik)
            }}
            // enableReinitialize={true}
            validateOnBlur={true}
            validateOnChange={true}
          >
            {(formik) => (
              <Form className="w-full">
                <div className="flex flex-1 justify-between">
                  <div className="flex flex-row w-full gap-[10px]">
                    <div className={style.inputField}>
                      <div className={style.labelAndButtonWrapper}>
                        <label className={style.inputLabel}>
                          Дата
                        </label>
                      </div>
                      <Field
                        value={formik?.values?.selectorData || null}
                        component={SelectCustomFormik}
                        name="selectorData"
                        isClearable={true}
                        placeholder={"Выберите дату"}
                        size="dm"
                        options={[
                          { value: 'today', label: 'За сегодня' },
                          { value: 'week', label: 'За неделю' },
                          { value: 'month', label: 'За месяц' },
                          { value: 'halfYear', label: 'За пол года' },
                          { value: 'custom', label: 'Своя дата' },
                        ]}
                        onChange={(options: any) => {
                          formik.setFieldValue("selectorData", options);
                        }}
                      />
                    </div>
                    {formik.values.selectorData == "custom" ? (
                      <div className={style.inputField}>
                        <div className={style.labelAndButtonWrapper}>
                          <label className={style.inputLabel2}>
                            {t(
                              "Дата от/до",
                            )}
                          </label>
                        </div>
                        <CalendarSelectFormik
                          dataObject={dataObject}
                          setDataObject={setDataObject}
                          maxRangeMonths={6}
                          onChange={() => {

                          }}
                          name="calendar"
                        />
                      </div>
                    ) : null}

                  </div>
                  <div className="self-end mb-[8px] flex justify-between flex-row gap-[10px]">
                    <button
                      type="button"
                      className="bg-bg w-auto text-gray-500 font-semibold py-2 px-4 h-[40px] rounded  whitespace-nowrap"
                      onClick={() => {
                        const week = getWeekFilter();
                        setIsLoading(true);
                        setIsDataLoaded(false);
                        setData(null); // Очищаем текущие данные графиков
                        setStartDate(undefined);
                        setEndDate(undefined);
                        setDataObject({
                          startDate: undefined,
                          endDate: undefined,
                          startTime: "",
                          endTime: ""
                        });
                        setFilterData(week);
                        setInitialValues({
                          selectorData: "week",
                          pickerData: null,
                        });
                        formik.resetForm({
                          values: {
                            selectorData: "week",
                            pickerData: null,
                          },
                        });
                      }}
                    >
                      {t("Очистить фильтр")}
                    </button>
                    <button
                      type="submit"
                      className="bg-mainColor text-white font-semibold py-2 px-4 h-[40px] rounded whitespace-nowrap"
                    >
                      {t("Применить изменения")}
                    </button>
                    <button
                      type="button"
                      onClick={() => { 
                        console.log('Export data:', {
                          title: getReportTitle(),
                          bins: data?.binOfd,
                          receivedChecks: data?.countSuccess,
                          labelsOrb: data?.labelsOrb,
                          seriesBar: data?.seriesBar,
                          ofdList,
                          countGroup: data?.countGroup
                        });
                        exportToExcel(getReportTitle(), data?.binOfd, data?.countSuccess, data?.labelsOrb, data?.seriesBar, ofdList, data?.countGroup) 
                      }}
                      className="bg-mainColor text-white font-semibold py-2 px-4 h-[40px] rounded whitespace-nowrap"
                    >
                      {t("Выгрузить в Exel")}
                    </button>

                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      <div className="rounded-lg overflow-x-auto  bg-white">
        <table className="max-w-full w-full leading-normal">
          <thead className="border-b-2 border-gray-300">
            <tr >
              <th
                className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                scope="col"
              >
                Название ОФД
              </th>
              <th
                className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                scope="col"
              >
                Полученные чеки от ОФД
              </th>

              <th
                className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200 "
                scope="col"
              >
                Успешно отправленные чеки принимающим ИС
              </th>
              <th
                className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                scope="col"
              >
                Общее количество отправленных/ошибочных чеков
              </th>
            </tr>
          </thead>
          {(() => {
            const showLoader = isLoading || !isDataLoaded;
            console.log("🔴 Рендер таблицы: isLoading=", isLoading, "isDataLoaded=", isDataLoaded, "showLoader=", showLoader, "data?.binOfd=", data?.binOfd);
            if (showLoader) {
              return (
                <tbody key="loader">
                  <tr>
                    <td colSpan={4} className="text-center py-10">
                      <TableLoader />
                    </td>
                  </tr>
                </tbody>
              );
            }
            if ([1].length === 0 || ![1]?.values) {
              return (
                <tbody key="no-users">
                  <tr>
                    <td colSpan={8} className="text-center py-5">
                      <div>
                        <EmptyListPlaceholderContent
                          title={t("Пользователей не найдено")}
                        />
                      </div>
                    </td>
                  </tr>
                </tbody>
              );
            }
            const showNoData = isDataLoaded && (!data?.binOfd || (Array.isArray(data.binOfd) && data.binOfd.length === 0));
            console.log("🔴 Рендер данных: showNoData=", showNoData, "data?.binOfd=", data?.binOfd);
            return (
              <tbody key="data" className="font-normal text-center">
                {showNoData && (
                <tr
                  className="cursor-pointer"
                  onClick={() => { }}
                >
                  <td colSpan={4}>
                    <div className="flex justify-center py-4">
                      <EmptyListPlaceholderContent title={'За данный период нет данных'} />
                    </div>
                  </td>
                </tr>
              )}
              {data?.binOfd?.map((value: any, index: any) => (
                <tr
                  key={`tr-${index}-${value.id}`}
                  className="cursor-pointer"
                  onClick={() => { }}
                >

                  <td className="px-5 py-5 text-[16px] font-[600] border-b border-gray-200 text-gray-600">
                    {ofdList[value]}
                    {/* {value.email || "-"} */}
                  </td>
                  <td className="px-1 py-5 text-sm border-b border-gray-200 text-gray-600">
                    <div className="flex justify-center items-center h-full">
                      <ReactApexChart
                        options={barOptions.options}
                        series={[
                          {
                            data: [(data?.countSuccess?.[value] ?? 0), (data?.countError?.[value] ?? 0)] // Значения для "Успешные" и "Ошибочные"
                          }
                        ]}
                        type="bar"
                        height={280}
                        width={200}
                      />
                    </div>
                  </td>
                  <td className="px-1 py-5 text-sm border-b border-gray-200 text-gray-600 text-center">
                    <div className="m-auto  items-center h-full">
                      <ReactApexChart
                        options={(() => {
                          return {
                            ...chartOptions.options,
                            labels: data?.labelsOrb[index] || [],
                          };
                        })()}
                        series={data?.seriesOrb[index] || []}
                        label={data?.labels}
                        type="polarArea"
                        height={280}
                      // width={400}
                      />
                    </div>
                  </td>
                  <td className="px-1 w-[40%] py-5 text-sm border-b border-gray-200 text-gray-600 text-center">
                    <div className="m-auto  items-center h-full">
                      <ReactApexChart
                        options={(() => {
                          return {
                            ...barDifferenceConfig.options,
                            xaxis: {
                              categories: data?.labelsOrb[index] || [],
                              tickAmount: 7,
                            },
                          };
                        })()}
                        series={data?.seriesBar[index] || []}
                        type="bar"
                        height={280} />
                    </div>
                  </td>

                </tr>
              ))}
              </tbody>
            );
          })()}
        </table>
      </div>
      <AlertBox isOpen={alertOpen} setIsOpen={setAlertOpen} />
    </div>
  );
}

export default MainScreen;